/**
 * GroupDetailPanel
 *
 * Slide-over panel showing details for a selected group.
 * Tabs: Members | Policy | Audit
 *
 * Props:
 *   group          {Object}   - Group document
 *   orgId          {string}   - Organisation ID
 *   onClose        {function} - Called when panel closes
 *   onGroupUpdated {function} - Called after membership/policy changes
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Clock, RefreshCw, Search, UserMinus, Users, X } from "lucide-react";
import useFeedback from "../../hooks/useFeedback";
import {
  addGroupMember,
  getGroupAuditLog,
  listGroupMembers,
  removeGroupMember,
} from "../../api/adminGroups";
import { AuthError } from "../../api";
import { syncGoogleWorkspaceGroup } from "../../api/adminGoogleWorkspace";
import { listAdminUsers } from "../../api/adminUsers";
import PolicyEditor from "./PolicyEditor";
import SourceBadge from "../Labels/SourceBadge";
import Loader from "../Loader/Loader";
import useClickOutside from "../../hooks/useClickOutside";
import logger from "../../services/Logger";

// ─── Tab bar ──────────────────────────────────────────────────────────────────

const TABS = ["Members", "Policy", "Audit"];

const TabBar = ({ active, onChange }) => (
  <div className="flex border-b border-edge-border dark:border-edge-gray-700">
    {TABS.map((tab) => (
      <button
        key={tab}
        onClick={() => onChange(tab)}
        className={`px-4 py-3 text-sm font-medium transition-colors ${
          active === tab
            ? "text-ink-gradient-primary border-b-2 border-edge-gradient-primary -mb-px"
            : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-ink dark:hover:text-ink-white"
        }`}
      >
        {tab}
      </button>
    ))}
  </div>
);

// ─── Members tab ──────────────────────────────────────────────────────────────

const MembersTab = ({ group, orgId, onGroupUpdated }) => {
  const { showFeedback } = useFeedback();
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [addingId, setAddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const addDropdownRef = useRef(null);
  const addButtonRef = useRef(null);
  const mountedRef = useRef(true);

  useClickOutside([addDropdownRef, addButtonRef], () => setAddOpen(false), addOpen);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersData, usersData] = await Promise.all([
        listGroupMembers(group.group_id, orgId),
        listAdminUsers(),
      ]);
      if (!mountedRef.current) return;
      setMembers(membersData ?? []);
      setAllUsers(usersData ?? []);
    } catch (err) {
      if (!(err instanceof AuthError)) {
        logger.error("GroupDetailPanel", "Error loading members", { error: err.message });
        showFeedback("Failed to load members.", "error");
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [group.group_id, orgId, showFeedback]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const userMap = Object.fromEntries((allUsers ?? []).map((u) => [u.user_id, u]));
  const memberIds = new Set(members.map((m) => m.user_id));

  const candidateUsers = (allUsers ?? []).filter(
    (u) => !memberIds.has(u.user_id) && (
      !addQuery ||
      (u.display_name || "").toLowerCase().includes(addQuery.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(addQuery.toLowerCase())
    )
  );

  const handleAdd = async (userId) => {
    setAddingId(userId);
    try {
      await addGroupMember(group.group_id, userId);
      setAddOpen(false);
      setAddQuery("");
      await fetchData();
      if (onGroupUpdated) onGroupUpdated();
      showFeedback("Member added.", "success");
    } catch {
      showFeedback("Failed to add member.", "error");
    } finally {
      if (mountedRef.current) setAddingId(null);
    }
  };

  const handleRemove = async (userId) => {
    setRemovingId(userId);
    try {
      await removeGroupMember(group.group_id, userId);
      await fetchData();
      if (onGroupUpdated) onGroupUpdated();
      showFeedback("Member removed.", "success");
    } catch {
      showFeedback("Failed to remove member.", "error");
    } finally {
      if (mountedRef.current) setRemovingId(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncGoogleWorkspaceGroup(group.group_id);
      showFeedback(`Sync complete — ${result.added} added, ${result.removed} removed.`, "success");
      await fetchData();
      if (onGroupUpdated) onGroupUpdated();
    } catch (err) {
      if (err?.response?.status === 429) {
        showFeedback("Sync rate limited. Please wait 60 seconds before syncing again.", "error");
      } else {
        showFeedback("Failed to sync group.", "error");
      }
    } finally {
      if (mountedRef.current) setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Add member button */}
      {group.source === "photon_native" && (
        <div className="relative">
          <button
            ref={addButtonRef}
            onClick={() => setAddOpen((v) => !v)}
            className="flex items-center gap-1.5 text-sm text-ink-gradient-primary hover:underline"
          >
            <Users className="w-4 h-4" />
            Add member
            <ChevronDown className="w-3 h-3" />
          </button>

          <AnimatePresence>
            {addOpen && (
              <motion.div
                ref={addDropdownRef}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="absolute top-8 left-0 z-50 w-72 bg-surface-dialog border border-edge-border dark:border-edge-gray-700 rounded-xl shadow-lg overflow-hidden"
              >
                <div className="p-2 border-b border-edge-border dark:border-edge-gray-700">
                  <div className="flex items-center gap-2 bg-fill-navigation-hover dark:bg-fill-navigation-surface-dark rounded-lg px-2 py-1.5">
                    <Search className="w-3 h-3 text-ink-gray-400 flex-shrink-0" />
                    <input
                      autoFocus
                      value={addQuery}
                      onChange={(e) => setAddQuery(e.target.value)}
                      placeholder="Search users…"
                      className="flex-1 text-xs bg-transparent outline-none text-ink-ink dark:text-ink-white placeholder-ink-gray-400"
                    />
                  </div>
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {candidateUsers.length === 0 ? (
                    <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 p-3">
                      {addQuery ? "No users match." : "All org users are already members."}
                    </p>
                  ) : (
                    candidateUsers.map((u) => (
                      <button
                        key={u.user_id}
                        onClick={() => handleAdd(u.user_id)}
                        disabled={addingId === u.user_id}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-fill-navigation-hover dark:hover:bg-fill-navigation-surface-dark text-left transition-colors"
                      >
                        <div className="w-6 h-6 rounded-full bg-fill-gradient-primary flex items-center justify-center text-ink-on-primary text-[10px] font-bold flex-shrink-0">
                          {(u.display_name || u.email || "?")[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-ink-ink dark:text-ink-white truncate">
                            {u.display_name || u.email || u.user_id}
                          </p>
                          {u.email && u.display_name && (
                            <p className="text-[10px] text-ink-gray-500 dark:text-ink-gray-400 truncate">{u.email}</p>
                          )}
                        </div>
                        {addingId === u.user_id && <Loader className="w-3 h-3" />}
                      </button>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Sync button — Google Workspace groups only */}
      {group.source === "google_workspace" && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
            Memberships are resolved at login. Use sync to refresh active members now.
          </p>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-1.5 text-sm font-medium text-ink-orange-600 dark:text-ink-orange-300 hover:underline disabled:opacity-50 flex-shrink-0 ml-4"
          >
            {syncing ? (
              <Loader className="w-4 h-4" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        </div>
      )}

      {/* Member list */}
      {members.length === 0 ? (
        <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 py-4 text-center">
          {group.source === "photon_native"
            ? "No members yet. Use 'Add member' to add someone."
            : group.source === "google_workspace"
            ? "No members yet. Use 'Sync now' to pull members from Google Workspace."
            : "No members yet. Members are synced via IdP SCIM."}
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-edge-border dark:divide-edge-gray-700">
          {members.map((m) => {
            const user = userMap[m.user_id];
            return (
              <div key={m.user_id} className="flex items-center gap-3 py-3">
                <div className="w-8 h-8 rounded-full bg-fill-gradient-primary flex items-center justify-center text-ink-on-primary text-xs font-bold flex-shrink-0">
                  {(user?.display_name || user?.email || m.user_id)[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink-ink dark:text-ink-white truncate">
                    {user?.display_name || user?.email || m.user_id}
                  </p>
                  {user?.email && user?.display_name && (
                    <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 truncate">{user.email}</p>
                  )}
                </div>
                <SourceBadge source={m.source} size="sm" />
                {m.source === "manual" && group.source === "photon_native" && (
                  <button
                    onClick={() => handleRemove(m.user_id)}
                    disabled={removingId === m.user_id}
                    className="p-1.5 rounded-lg text-ink-gray-400 hover:text-ink-red-500 hover:bg-fill-red-50 dark:hover:bg-fill-red-900/20 transition-colors disabled:opacity-50"
                    title="Remove member"
                  >
                    {removingId === m.user_id ? (
                      <Loader className="w-3 h-3" />
                    ) : (
                      <UserMinus className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Audit tab ────────────────────────────────────────────────────────────────

const ACTION_LABELS = {
  "group.created": "Group created",
  "group.updated": "Group updated",
  "group.deleted": "Group deleted",
  "group.member_added": "Member added",
  "group.member_removed": "Member removed",
  "group.policy_updated": "Policy updated",
  "group.default_policy_updated": "Default policy updated",
  "group.gw_registered": "Google Workspace group registered",
  "group.synced": "Group synced",
  group_created: "Group created",
  group_deleted: "Group deleted",
  member_added: "Member added",
  member_removed: "Member removed",
  policy_set: "Policy updated",
  group_synced: "Group synced",
};

const AuditTab = ({ group, orgId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!group?.group_id || !orgId) return;
    setLoading(true);
    setError(null);
    getGroupAuditLog(group.group_id, orgId)
      .then(setEvents)
      .catch((err) => {
        logger.error("AuditTab", "Failed to load audit log", { error: err.message });
        setError("Failed to load audit log.");
      })
      .finally(() => setLoading(false));
  }, [group?.group_id, orgId]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-ink-red-500">{error}</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400">No audit events yet.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0">
      {events.map((evt, i) => {
        const eventType = evt.event_type || evt.action;
        const timestamp = evt.timestamp || evt.created_at;
        const actorLabel = evt.actor_name || evt.actor_email || evt.actor_id;
        return (
          <div
            key={evt.event_id || i}
            className="flex items-start gap-3 py-3 border-b border-edge-border dark:border-edge-gray-700 last:border-0"
          >
            <div className="w-7 h-7 rounded-full bg-fill-add-surface dark:bg-fill-hex-2a3a4a flex items-center justify-center flex-shrink-0 mt-0.5">
              <Clock className="w-3.5 h-3.5 text-ink-gradient-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-ink-ink dark:text-ink-white">
                {ACTION_LABELS[eventType] ?? eventType}
              </p>
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
                {timestamp ? new Date(timestamp).toLocaleString() : ""}
                {actorLabel ? ` · ${actorLabel}` : ""}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── GroupDetailPanel ─────────────────────────────────────────────────────────

const GroupDetailPanel = ({ group, orgId, onClose, onGroupUpdated }) => {
  const { showFeedback } = useFeedback();
  const [activeTab, setActiveTab] = useState("Members");
  const [policyIsSaving, setPolicyIsSaving] = useState(false);
  const [policyIsDirty, setPolicyIsDirty] = useState(false);
  const panelRef = useRef(null);
  const policyEditorRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-surface-overlay/30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Panel */}
      <motion.div
        ref={panelRef}
        className="relative z-10 w-full max-w-[520px] h-full bg-surface-detail shadow-2xl flex flex-col overflow-hidden"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.25 }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-edge-border dark:border-edge-gray-700 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-ink-ink dark:text-ink-white truncate">
                {group.display_name || group.name || group.group_id}
              </h2>
              <SourceBadge source={group.source} size="sm" />
            </div>
            {group.external_id && group.external_id !== (group.display_name || group.name) && (
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5 truncate">{group.external_id}</p>
            )}
            {group.description && (
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5 truncate">{group.description}</p>
            )}
            <p className="text-[10px] text-ink-gray-400 dark:text-ink-gray-500 mt-0.5 font-mono">{group.group_id}</p>
          </div>
          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-ink-gray-400 hover:text-ink-gray-600 dark:hover:text-ink-white hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <TabBar active={activeTab} onChange={setActiveTab} />

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === "Members" && (
            <MembersTab group={group} orgId={orgId} onGroupUpdated={onGroupUpdated} />
          )}
          {activeTab === "Policy" && (
            <>
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => policyEditorRef.current?.save()}
                  disabled={policyIsSaving || !policyIsDirty}
                  className="px-3 py-1.5 text-sm font-medium text-ink-on-primary bg-fill-gradient-primary hover:bg-fill-primary-hover-vivid disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  {policyIsSaving ? "Saving…" : "Save Policy"}
                </button>
              </div>
            <PolicyEditor
              ref={policyEditorRef}
              groupId={group.group_id}
              orgId={orgId}
              onSavingChange={setPolicyIsSaving}
              onDirtyChange={setPolicyIsDirty}
              onSaved={() => {
                showFeedback("Policy saved.", "success");
                if (onGroupUpdated) onGroupUpdated();
              }}
            />
            </>
          )}
          {activeTab === "Audit" && (
            <AuditTab group={group} orgId={orgId} />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default GroupDetailPanel;
